import type { MenuEntry, MenuSection, RestaurantBrand } from '../types/restaurant';

export const arabAliBrand: RestaurantBrand = {
  name: 'Arbali Hotel',
  subtitle: 'Digital Menu',
  location: 'Harar-Ethiopia',
  phoneNumber: '+251 911 000 000',
  coverImage: '/arab-ali/cover.jpeg',
  menuUrl: 'https://arbali.ethio-viral.com/menu',
};

const createEntry = (
  sectionId: string,
  name: string,
  price: number,
  description?: string,
  needsVerification?: boolean,
): MenuEntry => ({
  id: `${sectionId}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`,
  name,
  price,
  sectionId,
  description,
  needsVerification,
});

export const arabAliMenuSections: MenuSection[] = [
  {
    id: 'breakfast',
    title: 'Breakfast',
    shortDescription: 'Morning staples, ferfer, foul, toast, oats, and egg dishes.',
    coverImage: '/arab-ali/breakfast.jpeg',
    items: [
      createEntry('breakfast', 'Omelet', 200),
      createEntry('breakfast', 'Fried Egg', 200),
      createEntry('breakfast', 'Cheese Omelet', 250),
      createEntry('breakfast', 'Special Omelet', 350),
      createEntry('breakfast', 'Scramble Eggs', 200),
      createEntry('breakfast', 'Fetira', 250),
      createEntry('breakfast', 'Melewa', 200),
      createEntry('breakfast', 'Chechebsa', 250),
      createEntry('breakfast', 'Normal Ferfer', 200),
      createEntry('breakfast', 'Special Ferfer', 450),
      createEntry('breakfast', 'Tebes Ferfer', 400),
      createEntry('breakfast', 'Tuna Ferfer', 400),
      createEntry('breakfast', 'French Toast', 250),
      createEntry('breakfast', 'Pancake', 250),
      createEntry('breakfast', 'Special Foul', 200),
      createEntry('breakfast', 'Oats', 250),
      createEntry('breakfast', 'Oats with Milk', 280),
      createEntry('breakfast', 'Oats with Butter', 300),
      createEntry('breakfast', 'Oats with Peanut Butter', 300),
      createEntry('breakfast', 'Oats with Honey', 300),
      createEntry('breakfast', 'Egg with Scramble Bread', 300),
      createEntry('breakfast', 'Tomato Sauce', 200),
      createEntry('breakfast', 'Egg with Tomato Sauce', 250),
      createEntry('breakfast', 'Foul with Tomato Sauce', 150),
      createEntry('breakfast', 'Egg with Meat', 300),
      createEntry('breakfast', 'Egg with Tuna', 350),
      createEntry('breakfast', 'Foul with Tuna', 350),
    ],
  },
  {
    id: 'arabian-foods',
    title: 'Arabian Foods',
    shortDescription: 'Shawarma, chicken plates, roasts, and house specialties.',
    coverImage: '/arab-ali/arabian-foods.jpeg',
    items: [
      createEntry('arabian-foods', 'Shawarma Chicken with Chips', 450),
      createEntry('arabian-foods', 'Special Shawarma Chicken with Chip', 550),
      createEntry('arabian-foods', 'Arbali Shawarma Chicken with Chips', 650),
      createEntry('arabian-foods', 'Wing Chicken with Rice', 750),
      createEntry('arabian-foods', 'Crispy Chicken with Chips', 600),
      createEntry('arabian-foods', 'Chicken Special', 1200),
      createEntry('arabian-foods', 'Chicken Leg', 750),
      createEntry('arabian-foods', 'Chicken Kotelet', 700),
      createEntry('arabian-foods', 'Rosted Chicken Full', 2000),
      createEntry('arabian-foods', 'Rosted Chicken Half', 1000),
      createEntry('arabian-foods', 'Fried Chicken', 700),
      createEntry('arabian-foods', 'Steak Fried Chicken', 500),
      createEntry('arabian-foods', 'Grilled Chicken', 550),
      createEntry('arabian-foods', 'Chicken Breast', 550),
      createEntry('arabian-foods', 'Zurbian Full', 2200),
      createEntry('arabian-foods', 'Zurbian Half', 1100),
      createEntry('arabian-foods', 'Chicken Tibs', 600),
    ],
  },
  {
    id: 'fish',
    title: 'Fish',
    shortDescription: 'Fish plates and house-style seafood entries.',
    coverImage: '/arab-ali/arabian-foods.jpeg',
    items: [
      createEntry('fish', 'Fish Kotelet', 500),
      createEntry('fish', 'Grilled Fish', 500, undefined, true),
      createEntry('fish', 'Fish Gulash', 600),
      createEntry('fish', 'Special Fish', 650),
    ],
  },
  {
    id: 'stick',
    title: 'Stick',
    shortDescription: 'Quick-serve stick items from the photographed menu.',
    coverImage: '/arab-ali/arabian-foods.jpeg',
    items: [
      createEntry('stick', 'Grill Stick', 650),
      createEntry('stick', 'Paper Stick', 500),
      createEntry('stick', 'Cheese Stick', 700),
    ],
  },
  {
    id: 'beef',
    title: 'Beef',
    shortDescription: 'Large beef platters, tibs, steak, and Ethiopian favorites.',
    coverImage: '/arab-ali/beef.jpeg',
    items: [
      createEntry('beef', 'Special Arabali', 2500),
      createEntry('beef', 'Special Arabali Foul', 3000, undefined, true),
      createEntry('beef', 'Hanid Full', 2000),
      createEntry('beef', 'Hanid Half', 1000),
      createEntry('beef', 'Combo', 2500),
      createEntry('beef', 'Special Combo', 3000),
      createEntry('beef', 'Half Special Combo', 1500),
      createEntry('beef', 'Special Tibs', 600),
      createEntry('beef', 'Goat Tibs', 500),
      createEntry('beef', 'Flat Tibs', 550),
      createEntry('beef', 'Beef Chili', 500),
      createEntry('beef', 'Steak Alla Bismack', 500, undefined, true),
      createEntry('beef', 'Steak Paparizano', 550, undefined, true),
      createEntry('beef', 'Beef Straganof', 550, undefined, true),
      createEntry('beef', 'Abish', 250),
      createEntry('beef', 'Shiro', 200),
      createEntry('beef', 'Bozena', 300),
      createEntry('beef', 'Special Shiro', 400),
    ],
  },
  {
    id: 'extra',
    title: 'Extra',
    shortDescription: 'Sides, bread, sauces, and add-ons.',
    coverImage: '/arab-ali/beef.jpeg',
    items: [
      createEntry('extra', 'Tuna', 150),
      createEntry('extra', 'Cheese', 150),
      createEntry('extra', 'Ketchup', 80),
      createEntry('extra', 'Enjera', 50),
      createEntry('extra', 'Bread', 50),
      createEntry('extra', 'Mayonnaise', 80),
      createEntry('extra', 'Sauce', 100),
      createEntry('extra', 'Meat Sauce', 150),
    ],
  },
  {
    id: 'burger-sandwich',
    title: 'Burger & Sandwich',
    shortDescription: 'Burgers, sandwiches, and fried sandwich plates.',
    coverImage: '/arab-ali/burger-salad.jpeg',
    items: [
      createEntry('burger-sandwich', 'Beef Burger', 400),
      createEntry('burger-sandwich', 'Special Burger', 600),
      createEntry('burger-sandwich', 'Cheese Burger', 550),
      createEntry('burger-sandwich', 'Special Double Burger', 700),
      createEntry('burger-sandwich', 'Club Sandwich', 500),
      createEntry('burger-sandwich', 'Special Club Sandwich', 600),
      createEntry('burger-sandwich', 'Egg Sandwich', 200),
      createEntry('burger-sandwich', 'Vegetable Sandwich', 200),
      createEntry('burger-sandwich', 'Tuna Sandwich', 350),
      createEntry('burger-sandwich', 'Cheese Sandwich', 300),
      createEntry('burger-sandwich', 'Crispy Sandwich', 600),
    ],
  },
  {
    id: 'salad-vegetable',
    title: 'Salad & Vegetable',
    shortDescription: 'Fresh salads and cooked vegetable dishes.',
    coverImage: '/arab-ali/burger-salad.jpeg',
    items: [
      createEntry('salad-vegetable', 'Special Salad', 400),
      createEntry('salad-vegetable', 'Garden Salad', 300),
      createEntry('salad-vegetable', 'Cooked Vegetable', 200),
      createEntry('salad-vegetable', 'Consolona Mixed', 300, undefined, true),
    ],
  },
  {
    id: 'spaghetti-rice-penne',
    title: 'Spaghetti / Rice / Penne',
    shortDescription: 'Pasta and rice plates served with different sauces and proteins.',
    coverImage: '/arab-ali/pasta-soup.jpeg',
    items: [
      createEntry('spaghetti-rice-penne', 'Spaghetti / Rice / Penne with Tomato Sauce', 300),
      createEntry('spaghetti-rice-penne', 'Spaghetti / Rice / Penne with Meat Sauce', 350),
      createEntry('spaghetti-rice-penne', 'Spaghetti / Rice / Penne with Vegetable Sauce', 300),
      createEntry('spaghetti-rice-penne', 'Spaghetti / Rice / Penne with Tuna', 450),
      createEntry('spaghetti-rice-penne', 'Spaghetti / Rice / Penne with Stir-Fried Meat', 450),
      createEntry('spaghetti-rice-penne', 'Spaghetti / Rice / Penne with Carbonara', 450),
    ],
  },
  {
    id: 'soup',
    title: 'Soup',
    shortDescription: 'Vegetable, chicken, and house soups.',
    coverImage: '/arab-ali/pasta-soup.jpeg',
    items: [
      createEntry('soup', 'Vegetable Soup', 200),
      createEntry('soup', 'Chicken Soup', 300),
      createEntry('soup', 'Special Soup', 350),
      createEntry('soup', 'Carrot Soup', 200),
      createEntry('soup', 'Tomato Soup', 200),
    ],
  },
  {
    id: 'pizza',
    title: 'Pizza',
    shortDescription: 'Signature pizzas with the photographed house topping notes.',
    coverImage: '/arab-ali/pizza.jpeg',
    items: [
      createEntry(
        'pizza',
        'Special Pizza',
        650,
        'Tomato sauce, mozzarella cheese, minced beef, chicken, tuna, olive, mushroom, boiled egg, pineapple, onion, green pepper, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Tuna Pizza',
        600,
        'Tomato sauce, tuna, onion, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Mixed Pizza',
        600,
        'Tomato sauce, mozzarella cheese, minced beef, tuna, olive, mushroom, onion, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Vegetable Pizza',
        400,
        'Tomato sauce, mixed vegetable, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Chicken Pizza',
        600,
        'Tomato sauce, mozzarella cheese, minced chicken, onion, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Mushroom Pizza',
        500,
        'Tomato sauce, mozzarella cheese, mushroom, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Tuna Egg Pizza',
        600,
        'Tomato sauce, mozzarella cheese, tuna, egg, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Margherita Pizza',
        400,
        'Tomato sauce, mozzarella cheese, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Oriental Pizza',
        600,
        'Tomato sauce, mozzarella cheese, minced beef, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Tuna Pizza Extra Cheese',
        700,
        'Tomato sauce, mozzarella cheese, tuna, onion, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Vegetable Pizza Extra Cheese',
        500,
        'Tomato sauce, mozzarella cheese, mixed vegetable, garlic oil, oregano, chili sauce.',
      ),
      createEntry(
        'pizza',
        'Four Season Pizza',
        550,
        'Tomato sauce, mozzarella cheese, garlic oil, tuna, chicken, beef, vegetables, pineapple.',
      ),
    ],
  },
  {
    id: 'juice',
    title: 'Juice',
    shortDescription: 'Fresh juices and house juice combinations.',
    coverImage: '/arab-ali/drinks.jpeg',
    items: [
      createEntry('juice', 'Strawberry Juice', 250),
      createEntry('juice', 'Papaya Juice', 200),
      createEntry('juice', 'Avocado Juice', 200),
      createEntry('juice', 'Milk Shake Juice', 300),
      createEntry('juice', 'Spris Juice', 250, undefined, true),
      createEntry('juice', 'Mango Juice', 250),
      createEntry('juice', 'Orange Juice', 300),
      createEntry('juice', 'Special Juice', 300),
      createEntry('juice', 'Watermelon Juice', 200),
      createEntry('juice', 'Lemon Juice', 100),
    ],
  },
  {
    id: 'hot-drinks',
    title: 'Hot Drinks',
    shortDescription: 'Tea, coffee, milk, and classic hot drinks.',
    coverImage: '/arab-ali/drinks.jpeg',
    items: [
      createEntry('hot-drinks', 'Tea', 30),
      createEntry('hot-drinks', 'Coffee', 50),
      createEntry('hot-drinks', 'Spriso', 50, undefined, true),
      createEntry('hot-drinks', 'Macchiato', 50),
      createEntry('hot-drinks', 'Milk', 50),
      createEntry('hot-drinks', 'Keshir', 50),
      createEntry('hot-drinks', 'Keshir with Milk', 60),
      createEntry('hot-drinks', 'Coffee with Milk', 60),
      createEntry('hot-drinks', 'Tea with Milk', 60),
      createEntry('hot-drinks', 'Lemon Flavor Tea', 50),
      createEntry('hot-drinks', 'Peanut Tea', 60),
      createEntry('hot-drinks', 'Cappuccino', 60),
      createEntry('hot-drinks', 'Special Tea', 60),
    ],
  },
  {
    id: 'soft-drinks',
    title: 'Soft Drinks',
    shortDescription: 'Bottled drinks and water sizes.',
    coverImage: '/arab-ali/drinks.jpeg',
    items: [
      createEntry('soft-drinks', 'Soft Drinks (Coca & Pepsi, Cool)', 60),
      createEntry('soft-drinks', '2 Liter Water', 55),
      createEntry('soft-drinks', '1 Liter Water', 35),
      createEntry('soft-drinks', '0.6 Liter Water', 25),
    ],
  },
];

export const arabAliMenuEntries = arabAliMenuSections.flatMap((section) =>
  section.items.map((item) => ({ ...item, coverImage: item.coverImage ?? section.coverImage })),
);

export const getMenuEntryById = (id: string): MenuEntry | undefined =>
  arabAliMenuEntries.find((item) => item.id === id);

export const getMenuSectionById = (id: string): MenuSection | undefined =>
  arabAliMenuSections.find((section) => section.id === id);
